// =============================================================================
// SIREPPAR - Wizard Candidatura, Paso 1: Datos Personales
// =============================================================================

import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { candidatoService } from '../../../services/api';
import type { Candidato } from './CandidaturaWizardPage';

interface Props {
  candidato: Candidato;
  onSaved: () => void;
  onNext: () => void;
}

interface DniEstado {
  status: 'idle' | 'validando' | 'ok' | 'error' | 'inhabilitado';
  mensaje?: string;
  apellido?: string;
  nombre?: string;
  localidad?: string;
  circuito?: string;
}

const GENEROS = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'NO_BINARIO', label: 'No binario / X' },
];

const NACIONALIDADES = [
  'Argentina - nativa',
  'Argentina - por opción',
  'Argentina - naturalizada',
];


const EDAD_MINIMA = 30; // Mínimo para Intendente, confirmado por la usuaria.

function calcularEdad(fechaISO: string): number {
  const hoy = new Date();
  const nacimiento = new Date(fechaISO);
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const noCumplioAun = hoy.getMonth() < nacimiento.getMonth() ||
    (hoy.getMonth() === nacimiento.getMonth() && hoy.getDate() < nacimiento.getDate());
  if (noCumplioAun) edad--;
  return edad;
}

export default function Paso1DatosPersonales({ candidato, onSaved, onNext }: Props) {
  const [form, setForm] = useState({
    apellido: candidato.apellido || '',
    nombre: candidato.nombre || '',
    dni: candidato.dni || '',
    fechaNacimiento: candidato.fechaNacimiento ? candidato.fechaNacimiento.slice(0, 10) : '',
    genero: candidato.genero || '',
    nacionalidad: candidato.nacionalidad || '',
    telefono: candidato.telefono || '',
    email: candidato.email || '',
  });
  const [dniEstado, setDniEstado] = useState<DniEstado>({ status: candidato.dni ? 'ok' : 'idle' });
  const [guardando, setGuardando] = useState(false);
  const montado = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [subiendoFrente, setSubiendoFrente] = useState(false);
  const [subiendoDorso, setSubiendoDorso] = useState(false);

  const docFrente = candidato.documentos.find(d => d.tipo === 'DNI_FRENTE_CANDIDATO');
  const docDorso = candidato.documentos.find(d => d.tipo === 'DNI_DORSO_CANDIDATO');

  // --- Validación de DNI contra el padrón, con debounce de 400ms ---
  useEffect(() => {
    const dniLimpio = form.dni.replace(/\D/g, '');
    if (dniLimpio.length < 7 || dniLimpio.length > 8) {
      setDniEstado({ status: 'idle' });
      return;
    }
    setDniEstado({ status: 'validando' });
    const timer = setTimeout(async () => {
      try {
        const res = await candidatoService.verificarDni(dniLimpio);
        const data = res.data.data;
        if (!data.enPadron) {
          setDniEstado({ status: 'error', mensaje: data.motivoNoEncontrado || 'DNI no encontrado en el padrón' });
        } else if (data.inhabilitado) {
          setDniEstado({ status: 'inhabilitado', mensaje: data.motivoInhabilitacion, apellido: data.apellido, nombre: data.nombre, localidad: data.localidad, circuito: data.circuito });
        } else {
          setDniEstado({ status: 'ok', apellido: data.apellido, nombre: data.nombre, localidad: data.localidad, circuito: data.circuito });
        }
      } catch {
        setDniEstado({ status: 'error', mensaje: 'Error al validar el DNI' });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.dni]);

  const guardarAhora = async (avisar = false) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setGuardando(true);
    try {
      await candidatoService.actualizar(candidato.id, {
        ...form,
        dni: form.dni.replace(/\D/g, ''),
      });
      onSaved();
      if (avisar) toast.success('Borrador guardado');
    } catch {
      toast.error('No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  // --- Autosave con debounce de 800ms ---
  useEffect(() => {
    if (!montado.current) {
      montado.current = true;
      return;
    }
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { guardarAhora(); }, 800);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

    // Autocompleta apellido/nombre con el padrón apenas el DNI valida, sin pisar
  // lo que el usuario ya haya escrito a mano.
  useEffect(() => {
    if (dniEstado.status === 'ok' && dniEstado.apellido && dniEstado.nombre) {
      setForm(f => ({
        ...f,
        apellido: f.apellido.trim() ? f.apellido : dniEstado.apellido!,
        nombre: f.nombre.trim() ? f.nombre : dniEstado.nombre!,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dniEstado.status]);
  const usarDatosPadron = () => {
    setForm(f => ({ ...f, apellido: dniEstado.apellido || f.apellido, nombre: dniEstado.nombre || f.nombre }));
    toast.success('Datos del padrón cargados');
  };

  const subirArchivo = async (tipo: 'DNI_FRENTE_CANDIDATO' | 'DNI_DORSO_CANDIDATO', archivo: File) => {
    const setSubiendo = tipo === 'DNI_FRENTE_CANDIDATO' ? setSubiendoFrente : setSubiendoDorso;
    setSubiendo(true);
    try {
      await candidatoService.subirDocumento(candidato.id, tipo, archivo);
      toast.success('Archivo subido');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo subir el archivo');
    } finally {
      setSubiendo(false);
    }
  };

  const eliminarArchivo = async (documentoId: string) => {
    try {
      await candidatoService.eliminarDocumento(documentoId);
      toast.success('Documento eliminado');
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'No se pudo eliminar el documento');
    }
  };

  const edadInvalida = form.fechaNacimiento ? calcularEdad(form.fechaNacimiento) < EDAD_MINIMA : false;

  const puedeContinuar =
    (dniEstado.status === 'ok' || dniEstado.status === 'inhabilitado') &&
    form.apellido.trim() && form.nombre.trim() && form.fechaNacimiento && form.genero && form.nacionalidad &&
    !edadInvalida;

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800">
        Datos identificatorios del candidato conforme al artículo 77 inciso a) del Código Electoral. Todos los campos marcados con asterisco son obligatorios.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">Identificación</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">DNI *</label>
            <input
              type="text"
              value={form.dni}
              onChange={e => setForm(f => ({ ...f, dni: e.target.value.replace(/[^\d]/g, '') }))}
              maxLength={8}
              placeholder="Sin puntos ni espacios"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {dniEstado.status === 'validando' && (
              <p className="text-xs text-blue-600 mt-1">Verificando en el padrón...</p>
            )}
            {dniEstado.status === 'ok' && (
              <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
                ✓ DNI verificado en el padrón{dniEstado.circuito && ` — circuito ${dniEstado.circuito}`}{dniEstado.localidad && `, ${dniEstado.localidad}`}.
                {(dniEstado.apellido && dniEstado.apellido.toUpperCase() !== form.apellido.toUpperCase()) && (
                  <button type="button" onClick={usarDatosPadron} className="ml-2 underline font-medium">Usar datos del padrón ({dniEstado.apellido}, {dniEstado.nombre})</button>
                )}
              </div>
            )}
            {dniEstado.status === 'inhabilitado' && (
              <p className="text-xs text-red-700 mt-1 font-medium">⚠ Este DNI figura inhabilitado: {dniEstado.mensaje}. Se puede continuar cargando, el análisis formal lo hace la Secretaría Electoral.</p>
            )}
            {dniEstado.status === 'error' && (
              <p className="text-xs text-red-600 mt-1">✗ {dniEstado.mensaje}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
            <input
              type="text"
              value={form.apellido}
              onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre(s) *</label>
            <input
              type="text"
              value={form.nombre}
              onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de nacimiento *</label>
            <input
              type="date"
              value={form.fechaNacimiento}
              onChange={e => setForm(f => ({ ...f, fechaNacimiento: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {form.fechaNacimiento && calcularEdad(form.fechaNacimiento) < EDAD_MINIMA && (
              <p className="text-xs text-red-600 mt-1">✗ Tiene {calcularEdad(form.fechaNacimiento)} años — se exige un mínimo de {EDAD_MINIMA} años para Intendente.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">Datos complementarios</h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Género (según DNI) *</label>
            <select
              value={form.genero}
              onChange={e => setForm(f => ({ ...f, genero: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Seleccioná...</option>
              {GENEROS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nacionalidad *</label>
            <select
              value={form.nacionalidad}
              onChange={e => setForm(f => ({ ...f, nacionalidad: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Seleccioná...</option>
              {NACIONALIDADES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono de contacto</label>
            <input
              type="text"
              value={form.telefono}
              onChange={e => setForm(f => ({ ...f, telefono: e.target.value }))}
              placeholder="Opcional"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico personal</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="Opcional"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {candidato.dni && (
        <div className="bg-white rounded-xl shadow p-6">
          <h3 className="font-semibold text-gray-900 mb-4">DNI del candidato (frente y dorso)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(['DNI_FRENTE_CANDIDATO', 'DNI_DORSO_CANDIDATO'] as const).map(tipo => {
              const doc = tipo === 'DNI_FRENTE_CANDIDATO' ? docFrente : docDorso;
              const subiendo = tipo === 'DNI_FRENTE_CANDIDATO' ? subiendoFrente : subiendoDorso;
              return (
                <div key={tipo}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {tipo === 'DNI_FRENTE_CANDIDATO' ? 'Frente del DNI' : 'Dorso del DNI'}
                  </label>
                  {doc ? (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center justify-between text-sm gap-2">
                      <span className="text-green-800 truncate flex-1">📄 {doc.nombreOriginal}</span>
                      <label className="text-blue-600 hover:text-blue-800 cursor-pointer text-xs font-medium flex-shrink-0">
                        Reemplazar
                        <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden"
                          onChange={e => e.target.files?.[0] && subirArchivo(tipo, e.target.files[0])} />
                      </label>
                      <button type="button" onClick={() => eliminarArchivo(doc.id)} className="text-red-500 hover:text-red-700 flex-shrink-0" title="Eliminar">✕</button>
                    </div>
                  ) : (
                    <label className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-gray-500 text-sm cursor-pointer hover:border-blue-400 hover:bg-blue-50">
                      {subiendo ? 'Subiendo...' : '+ Subir archivo'}
                      <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" disabled={subiendo}
                        onChange={e => e.target.files?.[0] && subirArchivo(tipo, e.target.files[0])} />
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

<div className="flex items-center justify-between">
        <button onClick={() => guardarAhora(true)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm">
          {guardando ? 'Guardando...' : 'Guardar borrador'}
        </button>
        <button
          onClick={async () => { await guardarAhora(); onNext(); }}
          disabled={!puedeContinuar}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
        >
          Continuar al domicilio →
        </button>
      </div>
    </div>
  );
}
